import { useTranslation } from "@sagot/i18n";
import { Button, Field, Icon, Input, Sheet, SheetContent, Switch, toast } from "@sagot/ui";
import { useRef, useState } from "react";
import { Controller, FormProvider, useForm, useWatch, type Control } from "react-hook-form";
import { FormAlert } from "../../../auth/components/form-alert";
import { useErrorMessage } from "../../../lib/api-errors";
import { zodResolver } from "../../../lib/zod-resolver";
import { TypedConfirmDialog } from "../shared/typed-confirm-dialog";
import type { Action, ActionHttpMethod, ActionIntegration } from "../shared/types";
import { useActionSchema } from "./action-form";
import { AudienceField } from "./audience-field";
import { AvailabilityEditor } from "./availability-editor";
import { BookingFormEditor } from "./booking-form-editor";
import { isBookingAction } from "./availability-model";
import { HttpEditor } from "./http-editor";
import { ParamEditor } from "./param-editor";
import { confirmationDefault, formSchema, sampleInput, toCreate, toUpdate, toolNameFrom, type ActionFormValues } from "./schema-model";
import { TestPanel } from "./test-panel";
import { useActionMutations } from "./use-actions";
import { WhenToUseField } from "./when-to-use-field";

export interface EditorTarget {
  /** Existing action, or `null` for a new one from a template. */
  action: Action | null;
  integration: ActionIntegration;
  title: string;
  initial: ActionFormValues;
}

interface SwitchRowProps {
  control: Control<ActionFormValues>;
  name: "requires_confirmation" | "requires_identity";
  label: string;
  hint: string;
  disabled: boolean;
  /** Called when the person flips the switch (not when the form sets it). */
  onUserChange?: () => void;
}

function SwitchRow({ control, name, label, hint, disabled, onUserChange }: SwitchRowProps) {
  const id = `action-${name}`;
  return (
    <div className="flex items-start justify-between gap-4 p-4">
      <div>
        <label htmlFor={id} className="text-sm font-semibold">{label}</label>
        <p id={`${id}-hint`} className="mt-0.5 text-xs text-fg-subtle">{hint}</p>
      </div>
      <Controller control={control} name={name} render={({ field }) => (
        <Switch id={id} aria-describedby={`${id}-hint`} checked={field.value} onCheckedChange={(checked) => { onUserChange?.(); field.onChange(checked); }} disabled={disabled} className="mt-0.5" />
      )} />
    </div>
  );
}

interface ActionEditorProps {
  agentId: string;
  target: EditorTarget;
  canManage: boolean;
  onClose: () => void;
}

/** `action-drawer`: edit or create an action, test it live, delete it (custom only). */
export function ActionEditor({ agentId, target, canManage, onClose }: ActionEditorProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const mutations = useActionMutations(agentId);
  const [saved, setSaved] = useState<Action | null>(target.action);
  const [deleting, setDeleting] = useState(false);
  const builtin = saved?.type === "builtin";
  const form = useForm<ActionFormValues>({ resolver: zodResolver(useActionSchema(builtin, saved?.has_secret ?? false)), defaultValues: target.initial });
  const values = useWatch({ control: form.control });
  const disabled = !canManage;
  const isNew = saved === null;
  // "Confirm before running" follows the method (GET off, POST/PUT/PATCH on) until the person toggles it themselves.
  const confirmChosen = useRef(false);
  const onMethodChange = (method: ActionHttpMethod) => {
    if (isNew && !confirmChosen.current) form.setValue("requires_confirmation", confirmationDefault(method), { shouldDirty: true });
  };
  const confirmHint = builtin ? t("build.actions.editor.confirmHint") : t(values.method === "GET" ? "build.actions.editor.confirmHintRead" : "build.actions.editor.confirmHintWrite");
  const save = saved ? mutations.update : mutations.create;
  const submit = form.handleSubmit((data) => {
    const schema = formSchema(data);
    const done = (action: Action) => {
      setSaved(action);
      form.reset({ ...data, secret: "" });
      toast.success(t(isNew ? "build.actions.editor.created" : "build.actions.editor.saved"));
    };
    if (saved) mutations.update.mutate({ id: saved.id, body: toUpdate(data, saved, schema) }, { onSuccess: done });
    else if (schema) mutations.create.mutate(toCreate(data, target.integration, schema), { onSuccess: done });
  });
  const nameField = form.register("name", {
    onChange: (event: { target: { value: string } }) => {
      if (isNew && !form.formState.dirtyFields.tool_name) form.setValue("tool_name", toolNameFrom(event.target.value));
    },
  });
  return (
    <Sheet open onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent
        title={isNew ? t("build.actions.editor.newTitle", { template: target.title }) : (values.name ?? target.title)}
        description={isNew ? undefined : t("build.actions.editor.editTitle")}
        closeLabel={t("common.close")}
        className="max-w-2xl"
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            {saved && saved.deletable && canManage ? (
              <Button variant="ghost" className="text-danger" onClick={() => { setDeleting(true); }}>
                <Icon name="trash" />
                {t("build.actions.editor.delete")}
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="secondary" onClick={onClose}>{t("build.actions.editor.cancel")}</Button>
              {canManage ? (
                <Button type="submit" form="action-editor-form" loading={save.isPending}>
                  <Icon name="check" />
                  {t("build.actions.editor.save")}
                </Button>
              ) : null}
            </div>
          </div>
        }
      >
        <FormProvider {...form}>
          <form id="action-editor-form" noValidate className="space-y-6" onSubmit={(event) => void submit(event)}>
            {save.isError ? <FormAlert title={message(save.error)} /> : null}
            {disabled ? <p className="rounded-md bg-surface-2 p-3 text-xs text-fg-muted">{t("build.actions.editor.readOnly")}</p> : null}
            {builtin ? <p className="rounded-md bg-info-soft p-3 text-xs text-fg-muted">{t("build.actions.editor.builtinNote")}</p> : null}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label={t("build.actions.editor.name")} error={form.formState.errors.name?.message}>
                <Input disabled={disabled} {...nameField} />
              </Field>
              <Field label={t("build.actions.editor.toolName")} hint={t("build.actions.editor.toolNameHint")} error={form.formState.errors.tool_name?.message}>
                <Input className="font-mono text-xs" disabled={disabled || builtin} {...form.register("tool_name")} />
              </Field>
            </div>
            <WhenToUseField disabled={disabled} guideOpen={isNew && !builtin} />
            {builtin ? null : (
              <>
                <ParamEditor disabled={disabled} />
                {form.formState.errors.params?.message ? <p className="text-xs font-semibold text-danger" role="alert">{form.formState.errors.params.message}</p> : null}
                <HttpEditor disabled={disabled} hasSecret={saved?.has_secret ?? false} onMethodChange={onMethodChange} />
              </>
            )}
            <div className="divide-y divide-line rounded-lg border border-line">
              <SwitchRow control={form.control} name="requires_confirmation" label={t("build.actions.editor.confirm")} hint={confirmHint} disabled={disabled} onUserChange={() => { confirmChosen.current = true; }} />
              <SwitchRow control={form.control} name="requires_identity" label={t("build.actions.editor.identity")} hint={t("build.actions.editor.identityHint")} disabled={disabled} />
            </div>
            <AudienceField agentId={agentId} disabled={disabled} locked={saved?.locked ?? false} />
          </form>
        </FormProvider>
        {saved && isBookingAction(saved) ? (
          <div className="mt-6">
            <AvailabilityEditor key={saved.id} agentId={agentId} action={saved} canManage={canManage} onSaved={setSaved} />
          </div>
        ) : null}
        {saved && isBookingAction(saved) ? (
          <div className="mt-6">
            <BookingFormEditor key={`form-${saved.id}`} agentId={agentId} action={saved} canManage={canManage} onSaved={setSaved} />
          </div>
        ) : null}
        <div className="mt-6">
          <TestPanel
            key={saved?.id ?? "new"}
            action={saved}
            sample={sampleInput(saved ? formSchema(form.getValues()) : null)}
            canRun={canManage}
            running={mutations.test.isPending}
            result={mutations.test.data}
            error={mutations.test.error}
            onRun={(input) => { if (saved) mutations.test.mutate({ id: saved.id, input }); }}
          />
        </div>
        {saved ? (
          <TypedConfirmDialog
            open={deleting}
            onOpenChange={setDeleting}
            title={t("build.actions.editor.deleteTitle", { name: saved.name })}
            body={t("build.actions.editor.deleteBody")}
            confirmLabel={t("build.actions.editor.deleteConfirm")}
            expected={saved.name}
            submitLabel={t("build.actions.editor.delete")}
            pending={mutations.remove.isPending}
            error={mutations.remove.isError ? message(mutations.remove.error) : null}
            onConfirm={() => {
              mutations.remove.mutate(saved.id, { onSuccess: () => { toast.success(t("build.actions.editor.deleted")); onClose(); } });
            }}
          />
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
